// Horizonte de materialização das recorrentes: 31/dez do ANO SEGUINTE ao atual.
export function recurringHorizonISO(): string {
  const now = new Date()
  return `${now.getFullYear() + 1}-12-31`
}

// ---------------------------------------------------------------------------
// Ocorrências ON-DEMAND: a partir do agendamento da regra, quais datas a
// recorrente "cai" num determinado mês. Não materializa nada — é só cálculo.
// Âncora do dia de cobrança/alinhamento: next_date (fica no dia correto).
// ---------------------------------------------------------------------------
interface ScheduleRule {
  frequency: 'weekly' | 'monthly' | 'yearly'
  interval: number
  start_date: string | null
  next_date: string
  end_date: string | null
  /** Semanal em dias escolhidos (0=Dom..6=Sáb). Vazio/ausente = 1 dia/semana pela âncora. */
  days_of_week?: number[] | null
  /** Nº TOTAL de ocorrências desde o início (parcelado). null/ausente = sem fim. */
  max_occurrences?: number | null
}
function parse(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function lastDay(year: number, month0: number): number {
  return new Date(year, month0 + 1, 0).getDate()
}
function mod(a: number, n: number): number {
  return ((a % n) + n) % n
}

// Ocorrências "cruas" de um mês, SEM o teto de max_occurrences (só start/end/freq).
function rawOccurrencesInMonth(rule: ScheduleRule, year: number, month0: number): string[] {
  const anchor = parse(rule.next_date)
  const start = rule.start_date ? parse(rule.start_date) : null
  const end = rule.end_date ? parse(rule.end_date) : null
  const mStart = new Date(year, month0, 1)
  const mEnd = new Date(year, month0, lastDay(year, month0))
  const inRange = (d: Date) => (!start || d >= start) && (!end || d <= end)
  const interval = Math.max(1, rule.interval)
  const out: string[] = []

  if (rule.frequency === 'monthly') {
    const anchorIdx = anchor.getFullYear() * 12 + anchor.getMonth()
    const mIdx = year * 12 + month0
    if (mod(mIdx - anchorIdx, interval) === 0) {
      const d = new Date(year, month0, Math.min(anchor.getDate(), lastDay(year, month0)))
      if (inRange(d)) out.push(iso(d))
    }
  } else if (rule.frequency === 'yearly') {
    if (month0 === anchor.getMonth() && mod(year - anchor.getFullYear(), interval) === 0) {
      const d = new Date(year, month0, Math.min(anchor.getDate(), lastDay(year, month0)))
      if (inRange(d)) out.push(iso(d))
    }
  } else {
    const dows = (rule.days_of_week ?? []).filter((n) => n >= 0 && n <= 6)
    if (dows.length > 0) {
      // Semanal em DIAS ESCOLHIDOS (ex.: Ter+Sex). Cada dia marcado do mês entra,
      // desde que a SEMANA esteja ativa pelo interval (a cada N semanas contadas a
      // partir da âncora). interval 1 = toda semana. A semana é alinhada pelo
      // domingo, e o Math.round no diff de semanas é imune a fuso/horário de verão.
      const wk = start ?? anchor
      const anchorSunday = new Date(wk.getFullYear(), wk.getMonth(), wk.getDate() - wk.getDay())
      const want = new Set(dows)
      for (let day = 1; day <= lastDay(year, month0); day++) {
        const d = new Date(year, month0, day)
        if (!want.has(d.getDay()) || !inRange(d)) continue
        const dSunday = new Date(year, month0, day - d.getDay())
        const weeksDiff = Math.round((dSunday.getTime() - anchorSunday.getTime()) / (7 * 86400000))
        if (mod(weeksDiff, interval) === 0) out.push(iso(d))
      }
    } else {
      // weekly clássico: 1 dia por semana, passos de 7*interval dias a partir da âncora
      const stepMs = 7 * interval * 86400000
      let t = anchor.getTime()
      while (t > mStart.getTime()) t -= stepMs
      while (t <= mEnd.getTime()) {
        const d = new Date(t)
        if (d >= mStart && inRange(d)) out.push(iso(d))
        t += stepMs
      }
    }
  }
  return out
}

// Quantas ocorrências caem ANTES do mês (year, month0), desde o início. Usado para
// aplicar o teto de max_occurrences: se já houve N ocorrências, as seguintes somem.
function countOccurrencesBeforeMonth(rule: ScheduleRule, year: number, month0: number): number {
  const origin = parse(rule.start_date ?? rule.next_date)
  const targetIdx = year * 12 + month0
  let y = origin.getFullYear()
  let m0 = origin.getMonth()
  let count = 0
  // Passo mês a mês do início até o mês alvo. Cap de segurança (a projeção nunca
  // olha mais que ~alguns anos à frente; max_occurrences é sempre pequeno).
  for (let i = 0; i < 12 * 60 && y * 12 + m0 < targetIdx; i++) {
    count += rawOccurrencesInMonth(rule, y, m0).length
    m0++
    if (m0 > 11) {
      m0 = 0
      y++
    }
  }
  return count
}

// Ocorrências de um mês RESPEITANDO max_occurrences (parcelado): depois da N-ésima
// ocorrência desde o início, a recorrente para — mesmo sem end_date. Antes, a
// projeção ao vivo (Recorrentes/Orçamento/Relatório) IGNORAVA max_occurrences e
// repetia a regra para sempre (ex.: Unfcu 1250 USD ×4 aparecia em todo mês futuro).
export function occurrenceDatesInMonth(rule: ScheduleRule, year: number, month0: number): string[] {
  const raw = rawOccurrencesInMonth(rule, year, month0)
  const max = rule.max_occurrences
  if (max == null || raw.length === 0) return raw
  let idx = countOccurrencesBeforeMonth(rule, year, month0) // índice 0-based da 1ª do mês
  const out: string[] = []
  for (const d of raw) {
    if (idx < max) out.push(d)
    idx++
  }
  return out
}

// ---------------------------------------------------------------------------
// Status das ocorrências de uma regra num mês: quais já foram lançadas nas
// transações e quais ainda estão disponíveis. Casa por MÊS (não por data
// exata) porque o dia lançado/importado costuma diferir do dia calculado.
// Faz pareamento 1:1 por ordem para respeitar recorrentes semanais (N
// ocorrências no mês, mas talvez só algumas já lançadas).
// ---------------------------------------------------------------------------
export interface MonthTxLite {
  date: string
  account_id: string
  description: string | null
  recurring_id: string | null
}
export interface OccurrenceStatus {
  date: string
  included: boolean
}
export function monthOccurrenceStatus(
  rule: ScheduleRule & { id: string; template: { account_id: string; description: string | null } },
  monthTxs: MonthTxLite[],
  year: number,
  month0: number,
): OccurrenceStatus[] {
  const dates = occurrenceDatesInMonth(rule, year, month0)
  if (dates.length === 0) return []
  const desc = (rule.template.description ?? '').trim().toLowerCase()
  // candidatas do mês: recurring_id da regra OU mesma descrição — em QUALQUER conta.
  // (O plano/regra pode apontar uma conta, mas o pagamento real sair de outra — normal.)
  const matched = monthTxs.filter(
    (t) => t.recurring_id === rule.id || (desc !== '' && (t.description ?? '').trim().toLowerCase() === desc),
  ).length
  // as `matched` primeiras ocorrências (ordenadas) ficam "incluídas"
  return dates.map((date, i) => ({ date, included: i < matched }))
}

// Próxima ocorrência >= fromISO (varre até 3 anos à frente).
export function nextOccurrenceISO(rule: ScheduleRule, fromISO: string): string | null {
  const from = parse(fromISO)
  let year = from.getFullYear()
  let month0 = from.getMonth()
  for (let i = 0; i < 36; i++) {
    for (const d of occurrenceDatesInMonth(rule, year, month0)) if (d >= fromISO) return d
    month0++
    if (month0 > 11) {
      month0 = 0
      year++
    }
  }
  return null
}
